import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, AlertCircle, Users, Clock, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { AnalyticsIcon } from '../components/icons';

interface ResultsData {
  item_name: string;
  item_type: string;
  org_name: string;
  branding: {
    show_organization_logo: boolean;
    logo_position: string;
    primary_color: string;
  };
  analytics: {
    total_responses: number;
    avg_duration_ms: number | null;
  };
  filters: any;
}

// Use relative URL - nginx proxies /api to backend in all environments
const API_BASE = '/api';

const PublicOutreachResultsPage: React.FC = () => {
  const { shortId } = useParams<{ shortId: string }>();
  
  const [data, setData] = useState<ResultsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!shortId) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/outreach-unified/public/results/${shortId}`);
      setData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load results');
    } finally {
      setLoading(false);
    }
  }, [shortId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDuration = (ms: number | null): string => {
    if (!ms) return '—';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-secondary">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background-secondary">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-neutral-900 mb-2">Results Unavailable</h1>
          <p className="text-neutral-600">{error || 'Unable to load results'}</p>
        </div>
      </div>
    );
  }

  const primaryColor = data.branding.primary_color || '#2563eb';

  return (
    <div className="min-h-screen bg-background-secondary py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        {data.branding.show_organization_logo && (
          <div className={`mb-6 text-${data.branding.logo_position}`}>
            <span 
              className="inline-block px-4 py-2 rounded font-medium text-white"
              style={{ backgroundColor: primaryColor }}
            >
              {data.org_name}
            </span>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Title */}
          <div 
            className="p-6 border-b"
            style={{ borderColor: `${primaryColor}30` }}
          >
            <div className="flex items-center gap-3 mb-2">
              <AnalyticsIcon className="w-6 h-6" style={{ color: primaryColor }} />
              <h1 className="text-2xl font-bold text-neutral-900">
                {data.item_name}
              </h1>
            </div>
            <p className="text-neutral-500">
              {data.item_type.charAt(0).toUpperCase() + data.item_type.slice(1)} Results
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-6">
            <div className="text-center p-4 bg-background-secondary rounded-xl">
              <Users className="w-8 h-8 mx-auto mb-2" style={{ color: primaryColor }} />
              <div className="text-3xl font-bold text-neutral-900">
                {data.analytics.total_responses}
              </div>
              <div className="text-sm text-neutral-500">Responses</div>
            </div>

            <div className="text-center p-4 bg-background-secondary rounded-xl">
              <Clock className="w-8 h-8 mx-auto mb-2" style={{ color: primaryColor }} />
              <div className="text-3xl font-bold text-neutral-900">
                {formatDuration(data.analytics.avg_duration_ms)}
              </div>
              <div className="text-sm text-neutral-500">Avg. Time</div>
            </div>

            <div className="text-center p-4 bg-background-secondary rounded-xl">
              <TrendingUp className="w-8 h-8 mx-auto mb-2" style={{ color: primaryColor }} />
              <div className="text-3xl font-bold text-neutral-900">
                {data.analytics.total_responses > 0 ? '100%' : '0%'}
              </div>
              <div className="text-sm text-neutral-500">Completion Rate</div>
            </div>
          </div>

          {/* Message */}
          <div className="p-6 border-t text-center text-neutral-500">
            <p>
              These results were shared from Capitol Commons.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-neutral-500">
          Powered by Capitol Commons
        </div>
      </div>
    </div>
  );
};

export default PublicOutreachResultsPage;

